import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { eventToLayoutBooking, isActivityLayoutBooking } from '../lib/activityLayoutBooking';
axios.defaults.withCredentials = true;

const yyyyMMdd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const parseYmd = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return new Date();
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const pickHM = (v) => {
  if (!v) return '';
  const m = String(v).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
};

/** 預約人數（每位各一張可拖拉的牌） */
function bookingHeadcount(b) {
  const n = Number(b?.NUMBER);
  if (Number.isFinite(n) && n >= 1) return Math.min(50, Math.floor(n));
  return 1;
}

/**
 * 預約牌張數（未放置左側列表）：座位名稱為 1F 者每筆 1 張；其餘（2F、夜貓等）依人數。
 * 非 1F 預約拖到 1F 座位格時可同一格疊多張（×N），與本邏輯無衝突。
 */
function bookingCardCountForFloor(b, floorTab) {
  void floorTab;
  if (isActivityLayoutBooking(b)) return 1;
  if (isBookingSeatName1F(b)) return 1;
  return bookingHeadcount(b);
}

function isBookingSeatName1F(b) {
  const seatName = String(b?.SEAT_NAME || '').trim();
  return /^1F/i.test(seatName);
}

function isSingleCardBooking(b) {
  return isActivityLayoutBooking(b) || isBookingSeatName1F(b);
}

/** 自訂 MIME，避免 text/plain 拖出視窗觸發瀏覽器搜尋 */
const SEAT_CARD_MIME = 'application/x-seat-card';

function normPlacementPersonSeq(p) {
  const n = Number(p?.PERSON_SEQ);
  if (Number.isFinite(n) && n >= 1) return Math.min(50, Math.floor(n));
  return 1;
}

function normDragPersonSeq(raw) {
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 1) return Math.min(50, Math.floor(n));
  return 1;
}

const SLOT_GROUPS = {
  left: Array.from({ length: 3 }).map((_, i) => ({ id: `L${i + 1}`, label: `高腳${i + 1}` })),
  right: Array.from({ length: 5 }).map((_, i) => ({ id: `R${i + 1}`, label: `面牆${i + 1}` })),
};

/** 2F-L 中間升降桌：每列 M1-k 與 M2-k 為一組對座（上排 1 組 + 下排 4 組） */
const MIDDLE_FACE_GROUPS = [
  { row: 1, title: '上排 · 對座（2 人位）' },
  ...[2, 3, 4, 5].map((row, i) => ({ row, title: `下排 · 對座 ${i + 1}` })),
].map(({ row, title }) => ({
  row,
  title,
  slots: [
    { id: `M1-${row}`, label: `升降桌${2 * row - 1}` },
    { id: `M2-${row}`, label: `升降桌${2 * row}` },
  ],
}));

/** 未放置排序：依客戶姓名（繁中）；同姓名再依預約 ID、人序 */
function customerNameSortKey(b) {
  return String(b?.CUSTOMER_NAME || '').replace(/\s+/g, ' ').trim();
}

function seatNameSortKey(b) {
  return String(b?.SEAT_NAME || '').replace(/\s+/g, ' ').trim();
}

function bookingStartSortKey(b) {
  const t = new Date(b?.START_AT || 0).getTime();
  if (Number.isFinite(t) && t > 0) return t;
  const hm = pickHM(b?.START_TIME || pickHM(b?.START_AT) || '');
  const m = String(hm || '').match(/^(\d{2}):(\d{2})$/);
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** 面窗 6 格 */
const FOCUS_WINDOW = Array.from({ length: 6 }).map((_, i) => ({ id: `F${i + 1}`, label: `面窗${i + 1}` }));
/** 大桌 8 格 */
const FOCUS_TABLE = Array.from({ length: 8 }).map((_, i) => ({ id: `F${i + 7}`, label: `大桌${i + 1}` }));

export default function SeatCardLayout() {
  const [day, setDay] = useState(() => yyyyMMdd(new Date()));
  /** 樓層：1F / 2F（分開顯示，用按鈕切換） */
  const [floorTab, setFloorTab] = useState('1f');
  const [boardTab, setBoardTab] = useState('seat'); // 'seat' | 'focus'（僅 2F）
  const [bookings, setBookings] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [zoneConfirm, setZoneConfirm] = useState({ seat: false, focus: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(new Set());
  const [autoBusy, setAutoBusy] = useState(false);
  const [zoneSaving, setZoneSaving] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  const [dayActivities, setDayActivities] = useState([]);

  const mountedRef = useRef(true);
  /** 進行中拖曳：未成功放到有效格則 dragend 回底部 dock */
  const dragSessionRef = useRef(null);

  const dayLabel = useMemo(() => {
    const d = parseYmd(day);
    return `${d.getMonth() + 1}/${d.getDate()}（${'日一二三四五六'[d.getDay()]}）`;
  }, [day]);

  /** 同一頁含 1F + 2F：未放置名單為當日全部有效預約 */
  const layoutBookings = useMemo(() => {
    const acts = (dayActivities || []).map(eventToLayoutBooking).filter(Boolean);
    return [...(bookings || []), ...acts];
  }, [bookings, dayActivities]);
  const bookingById = useMemo(() => {
    const m = new Map();
    for (const b of layoutBookings) m.set(Number(b.ID), b);
    return m;
  }, [layoutBookings]);

  /** 已放置的「預約#人序」鍵 */
  const placedPersonKeys = useMemo(() => {
    const s = new Set();
    for (const p of placements || []) {
      const bid = Number(p.BOOKING_ID);
      if (!Number.isFinite(bid)) continue;
      const ps = normPlacementPersonSeq(p);
      s.add(`${bid}:${ps}`);
    }
    return s;
  }, [placements]);

  /** 該預約是否已有任一擺放（不限樓層）；用於「座位名稱 1F」每筆僅一張 */
  const placedBookingIdsAny = useMemo(() => {
    const s = new Set();
    for (const p of placements || []) {
      const bid = Number(p.BOOKING_ID);
      if (!Number.isFinite(bid)) continue;
      const sid = String(p.SLOT_ID || '').trim();
      if (!sid) continue;
      s.add(bid);
    }
    return s;
  }, [placements]);

  const normalize1fSlotId = useCallback((slotIdRaw) => {
    if (slotIdRaw === null || slotIdRaw === undefined || slotIdRaw === '') return null;
    const sid = String(slotIdRaw || '').trim();
    if (!sid) return null;
    if (!/^1F-/i.test(sid)) return sid;
    // 1F A1/B1、A2/B2、A3/B3 合併為三個位子（向後相容舊資料）
    const m = /^1F-([AB])([123])$/i.exec(sid);
    if (m) return `1F-A${m[2]}B${m[2]}`;
    // 1F A8/B8 合併為 A8B8（向後相容舊資料）
    if (/^1F-A8$/i.test(sid) || /^1F-B8$/i.test(sid)) return '1F-A8B8';
    return sid;
  }, []);

  /** @type {Map<string, { bid: number, ps: number }[]>} 同一格可多人，順序同後端回傳 */
  const slotToEntries = useMemo(() => {
    const m = new Map();
    for (const p of placements || []) {
      const bid = Number(p.BOOKING_ID);
      if (!Number.isFinite(bid)) continue;
      if (!bookingById.has(bid)) continue;
      const sid = normalize1fSlotId(p.SLOT_ID);
      if (!sid) continue;
      const ps = normPlacementPersonSeq(p);
      if (!m.has(sid)) m.set(sid, []);
      m.get(sid).push({ bid, ps });
    }
    return m;
  }, [placements, bookingById, normalize1fSlotId]);

  const unplacedPersons = useMemo(() => {
    const rows = [];
    for (const b of layoutBookings || []) {
      const id = Number(b.ID);
      if (!Number.isFinite(id)) continue;

      // 未放置名單：不依樓層分頁篩選，全部顯示
      if (isSingleCardBooking(b)) {
        if (!placedBookingIdsAny.has(id)) rows.push({ b, personSeq: 1 });
        continue;
      }

      const hc = bookingHeadcount(b);
      for (let ps = 1; ps <= hc; ps += 1) {
        if (!placedPersonKeys.has(`${id}:${ps}`)) rows.push({ b, personSeq: ps });
      }
    }
    rows.sort((a, b) => {
      const sa = seatNameSortKey(a.b);
      const sb = seatNameSortKey(b.b);
      const bySeat = sa.localeCompare(sb, 'zh-Hant', { sensitivity: 'base' });
      if (bySeat !== 0) return bySeat;

      const ta = bookingStartSortKey(a.b);
      const tb = bookingStartSortKey(b.b);
      if (ta !== tb) return ta - tb;

      const na = customerNameSortKey(a.b);
      const nb = customerNameSortKey(b.b);
      const byName = na.localeCompare(nb, 'zh-Hant', { sensitivity: 'base' });
      if (byName !== 0) return byName;
      const ida = Number(a.b.ID);
      const idb = Number(b.b.ID);
      if (ida !== idb) return ida - idb;
      return a.personSeq - b.personSeq;
    });
    return rows;
  }, [layoutBookings, placedPersonKeys, placedBookingIdsAny]);

  const refresh = useCallback(async (d = day) => {
    setLoading(true);
    setErr('');
    try {
      const [bk, pl, bn] = await Promise.all([
        axios.get('/api/admin/seat-bookings', { params: { date: d } }),
        axios.get('/api/admin/seat-card-layout', { params: { date: d } }),
        axios.get('/api/banquet/today', { params: { date: d } }),
      ]);

      if (!mountedRef.current) return;

      const rows = bk?.data?.rows || [];
      const st = (s) => String(s || '').trim().toLowerCase();
      const filtered = rows.filter((r) => {
        const status = st(r.STATUS);
        if (status === 'cancelled' || status === 'rejected') {
          return Number(r.is_cancelled_today) === 1 || Number(r.is_rejected_today) === 1;
        }
        return true;
      });
      setBookings(filtered);

      setDayActivities(Array.isArray(bn?.data?.events) ? bn.data.events : []);
      setPlacements(pl?.data?.placements || []);
      const zc = pl?.data?.zoneConfirm;
      setZoneConfirm({
        seat: !!zc?.seat,
        focus: !!zc?.focus,
      });
    } catch (e) {
      if (!mountedRef.current) return;
      setErr(e?.response?.data?.message || '讀取失敗');
      setBookings([]);
      setDayActivities([]);
      setPlacements([]);
      setZoneConfirm({ seat: false, focus: false });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [day]);

  useEffect(() => {
    mountedRef.current = true;
    refresh(day);
    return () => { mountedRef.current = false; };
  }, [day, refresh]);

  useEffect(() => {
    const blockBrowserNav = (e) => {
      e.preventDefault();
    };
    document.addEventListener('dragover', blockBrowserNav);
    document.addEventListener('drop', blockBrowserNav);
    return () => {
      document.removeEventListener('dragover', blockBrowserNav);
      document.removeEventListener('drop', blockBrowserNav);
    };
  }, []);

  const saveKey = (bookingId, personSeq) => `${Number(bookingId)}:${normDragPersonSeq(personSeq)}`;

  const setSavingFlag = (bookingId, personSeq, on) => {
    const k = saveKey(bookingId, personSeq);
    setSaving((s) => {
      const next = new Set(s);
      if (on) next.add(k);
      else next.delete(k);
      return next;
    });
  };

  const postPlacement = async ({ bookingId, slotId, personSeq }) => {
    const normSlot = normalize1fSlotId(slotId);
    const bRow = bookingById.get(Number(bookingId));
    const ps = bRow && isSingleCardBooking(bRow) ? 1 : normDragPersonSeq(personSeq);
    setSavingFlag(bookingId, ps, true);
    try {
      const { data } = await axios.post('/api/admin/seat-card-layout', {
        date: day,
        booking_id: bookingId,
        slot_id: normSlot || null,
        person_seq: ps,
      });
      if (!data?.ok) throw new Error('save_failed');
      await refresh(day);
    } finally {
      setSavingFlag(bookingId, ps, false);
    }
  };

  const postZoneConfirm = async (nextSeat, nextFocus) => {
    setZoneSaving(true);
    try {
      const { data } = await axios.post('/api/admin/seat-card-layout/zone-confirm', {
        date: day,
        seat_confirmed: nextSeat,
        focus_confirmed: nextFocus,
      });
      if (data?.zoneConfirm) {
        setZoneConfirm({
          seat: !!data.zoneConfirm.seat,
          focus: !!data.zoneConfirm.focus,
        });
      } else {
        await refresh(day);
      }
    } catch {
      setErr('主管確認狀態儲存失敗');
    } finally {
      setZoneSaving(false);
    }
  };

  const runAutoLayout = async () => {
    setAutoBusy(true);
    setErr('');
    try {
      const { data } = await axios.post('/api/admin/seat-card-layout/auto', { date: day, apply: false });
      if (!data?.ok) throw new Error('preview_failed');
      const n = (data.placements || []).length;
      const sk = (data.skippedIds ?? data.overflowIds ?? []).length;
      const msg =
        '試排預覽：僅「座位名稱」與五種正式全名完全一致者才會擺放（空白正規化後比對）。' +
        ` 符合：${n} 筆；不符合規則不排：${sk} 筆。` +
        ' 五種全名為：2F-H 交誼廳高腳座位、2F-L 交誼廳升降桌座位、2F-P 交誼廳面牆個人位、2F-W 專注區面窗座位、2F-Q 專注區大桌座位。' +
        ' 中間升降桌試排：三組 2+4+4；同一區預約依人數大→小排；3～4 人只會落在同一個「4 人組」內連續位子；1～2 人先上排對座再兩個 4 人組；超過 4 人再沿列連續占格。' +
        ' 確定套用會清除當日既有擺放，只寫入上述符合者；同一格可多人。是否套用？';
      if (!window.confirm(msg)) return;
      const applyRes = await axios.post('/api/admin/seat-card-layout/auto', { date: day, apply: true });
      if (!applyRes.data?.ok) throw new Error('apply_failed');
      await refresh(day);
    } catch {
      setErr('自動試排失敗，請重試');
    } finally {
      setAutoBusy(false);
    }
  };

  const printLayout3Pages = async () => {
    if (printBusy) return;
    setPrintBusy(true);
    setErr('');
    try {
      const { data } = await axios.post('/api/admin/seat-card-layout/print-direct', { date: day });
      if (data?.printed) {
        const head = `已送印：預約牌擺放（3頁）（${data.date || day}）`;
        alert(data?.printHint ? `${head}\n\n${data.printHint}` : head);
      } else {
        alert(`PDF 已生成但未自動列印：\n${data?.file || ''}\n${data?.hint || ''}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '列印失敗';
      alert(`列印失敗：${msg}`);
    } finally {
      setPrintBusy(false);
    }
  };

  const onDragStartBooking = (e, bookingId, personSeq) => {
    const ps = normDragPersonSeq(personSeq);
    const payload = JSON.stringify({ booking_id: bookingId, person_seq: ps });
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData(SEAT_CARD_MIME, payload);
    } catch {
      /* some browsers reject custom MIME */
    }
    // 短字串避免拖出視窗變成搜尋關鍵字；實際資料以 dragSessionRef 為準
    e.dataTransfer.setData('text/plain', ' ');
    dragSessionRef.current = {
      bookingId: Number(bookingId),
      personSeq: ps,
      dropSucceeded: false,
    };
  };

  const markDropSucceeded = () => {
    if (dragSessionRef.current) dragSessionRef.current.dropSucceeded = true;
  };

  const onDragEndBooking = async () => {
    const sess = dragSessionRef.current;
    dragSessionRef.current = null;
    if (!sess || sess.dropSucceeded) return;
    const bookingId = Number(sess.bookingId);
    if (!Number.isFinite(bookingId)) return;
    try {
      await postPlacement({
        bookingId,
        slotId: null,
        personSeq: sess.personSeq,
      });
    } catch {
      /* 回 dock 失敗不阻斷 UI；下次 refresh 會校正 */
    }
  };

  const readDragPayload = (e) => {
    const sess = dragSessionRef.current;
    if (sess && Number.isFinite(Number(sess.bookingId))) {
      return {
        booking_id: Number(sess.bookingId),
        person_seq: normDragPersonSeq(sess.personSeq),
      };
    }
    try {
      let raw = '';
      try {
        raw = e.dataTransfer.getData(SEAT_CARD_MIME) || '';
      } catch {
        raw = '';
      }
      if (!raw || !String(raw).trim()) {
        raw = e.dataTransfer.getData('text/plain') || '';
      }
      const trimmed = String(raw).trim();
      if (!trimmed || trimmed === ' ') return null;
      const obj = JSON.parse(trimmed);
      const id = Number(obj?.booking_id);
      if (!Number.isFinite(id)) return null;
      return { booking_id: id, person_seq: normDragPersonSeq(obj?.person_seq) };
    } catch {
      return null;
    }
  };

  const DropZone = ({ title, children, onDropBooking, hint, dock = false }) => {
    return (
      <section style={dock ? st.zoneDock : st.zone}>
        <div style={dock ? st.zoneTitleRowDock : st.zoneTitleRow}>
          <div style={st.zoneTitle}>{title}</div>
          {hint ? <div style={st.zoneHint}>{hint}</div> : null}
        </div>
        <div
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const payload = readDragPayload(e);
            if (!payload) return;
            markDropSucceeded();
            onDropBooking?.(payload.booking_id, payload.person_seq);
          }}
          style={dock ? st.zoneBodyDock : st.zoneBody}
        >
          {children}
        </div>
      </section>
    );
  };

  /** @param {'full' | 'slot' | 'list'} variant */
  const BookingCard = ({ b, variant = 'full', personSeq = 1, slotStackMult = 1 }) => {
    const id = Number(b?.ID);
    const total = bookingCardCountForFloor(b, floorTab);
    const ps = Math.min(total, Math.max(1, Math.floor(Number(personSeq)) || 1));
    const sk = saveKey(id, ps);
    const isBusy = Number.isFinite(id) ? saving.has(sk) : false;
    const voidedToday = Number(b?.is_cancelled_today) === 1 || Number(b?.is_rejected_today) === 1;
    const t = b?.START_TIME || pickHM(b?.START_AT) || '';
    const phone = String(b?.PHONE || '').trim();
    const name = b?.CUSTOMER_NAME || '（未填姓名）';
    const seatName = String(b?.SEAT_NAME || '').trim();
    const noteText = String(b?.NOTE || '').trim();
    const noteTitle = noteText ? `備註：${noteText}` : '';
    const sm = Math.max(1, Math.floor(Number(slotStackMult)) || 1);
    const fracLabel = total > 1 ? `${ps}/${total}` : '';
    const is1fTab = String(floorTab || '').toLowerCase() === '1f';
    /** 座位格內：1F 顯示人數；2F 不顯示（夜貓在二樓以第 0 行幾分之幾為主） */
    const showSlotRow2Headcount = variant === 'slot' && is1fTab;
    const headcount = Math.max(1, Math.floor(Number(b?.NUMBER)) || 1);
    const pairLabelUi = String(b?.PAIR_LABEL_UI || '').trim();
    const pairLabelSpan = pairLabelUi ? (
      <span style={{ color: '#16a34a', fontWeight: 800, fontSize: 11, flex: '0 0 auto' }}>{pairLabelUi}</span>
    ) : null;

    if (variant === 'dock') {
      return (
        <div
          draggable={!isBusy && !voidedToday}
          onDragStart={(e) => onDragStartBooking(e, id, ps)}
          onDragEnd={onDragEndBooking}
          style={{
            ...st.cardDock,
            ...(voidedToday ? { border: '2px solid #ef4444', background: '#fee2e2' } : {}),
            opacity: isBusy ? 0.65 : 1,
            cursor: voidedToday || isBusy ? 'not-allowed' : 'grab',
          }}
          title={noteTitle || undefined}
        >
          <div style={st.cardDockLine1}>
            <span style={st.cardDockTime}>{t}</span>
            {fracLabel ? <span style={st.fracTagDock}>{fracLabel}</span> : null}
            {isActivityLayoutBooking(b) ? (
              <span style={st.cardDockHeadcount}>活動</span>
            ) : isBookingSeatName1F(b) ? (
              <span style={st.cardDockHeadcount}>人數 {bookingHeadcount(b)}</span>
            ) : null}
            <span style={st.cardDockName}>{name}</span>
            {pairLabelSpan}
            {voidedToday ? (
              <span style={st.voidBadgeDock}>
                {Number(b?.is_rejected_today) === 1 ? '拒' : '消'}
              </span>
            ) : null}
          </div>
          <div style={st.cardDockLine2}>
            <span style={st.cardDockSeat}>{seatName || '—'}</span>
            {phone ? <span style={st.cardDockPhone}>{phone}</span> : null}
          </div>
          {noteText ? (
            <div style={st.cardDockNote}>{noteText}</div>
          ) : null}
        </div>
      );
    }

    if (variant === 'slot') {
      const showRow0Stack = is1fTab && sm > 1;
      const showRow0Frac = !showRow0Stack && total > 1;
      return (
        <div
          draggable={!isBusy && !voidedToday}
          onDragStart={(e) => onDragStartBooking(e, id, ps)}
          onDragEnd={onDragEndBooking}
          style={{
            ...st.cardSlot,
            ...(voidedToday ? { border: '2px solid #ef4444', background: '#fee2e2' } : {}),
            opacity: isBusy ? 0.65 : 1,
            cursor: voidedToday || isBusy ? 'not-allowed' : 'grab',
          }}
          title={noteTitle || undefined}
        >
          <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
            {showRow0Stack ? (
              <div style={st.cardSlotRow0Frac}>
                <span style={st.fracTagRow0}>×{sm}</span>
              </div>
            ) : showRow0Frac ? (
              <div style={st.cardSlotRow0Frac}>
                <span style={st.fracTagRow0}>{fracLabel}</span>
              </div>
            ) : null}
            <div style={st.cardSlotTopLine}>
              <span style={st.cardSlotTimeInline}>{t}</span>
              <span style={st.cardSlotNameInline}>{name}</span>
              {pairLabelSpan}
              {voidedToday ? (
                <span style={st.voidBadgeSlot}>
                  {Number(b?.is_rejected_today) === 1 ? '今日已拒絕' : '今日臨時取消'}
                </span>
              ) : null}
            </div>
            <div style={st.cardSlotBottomLine}>
              {seatName ? <span style={st.cardSlotSeatInline}>{seatName}</span> : <span style={st.cardSlotSeatInline}>—</span>}
              {showSlotRow2Headcount ? (
                <span style={st.cardSlotHeadcountBadge}>人數 {headcount}</span>
              ) : null}
            </div>
            {noteText ? (
              <div style={st.cardSlotNoteInline}>
                <span style={st.cardNoteLabel}>備註</span>
                {noteText}
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    const metaRow = (
      <div style={{ marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
        <span style={{ fontSize: 12, fontWeight: 1000, color: '#374151' }}>{phone || '—'}</span>
        <span style={{ fontSize: 12, fontWeight: 900, color: '#374151' }}>{b?.SEAT_NAME || ''}</span>
        {b?.NUMBER != null ? (
          <span style={{ fontSize: 12, fontWeight: 900, color: '#374151' }}>人數：{b.NUMBER}</span>
        ) : null}
      </div>
    );

    return (
      <div
        draggable={!isBusy && !voidedToday}
        onDragStart={(e) => onDragStartBooking(e, id, ps)}
        onDragEnd={onDragEndBooking}
        style={{
          ...st.card,
          ...(voidedToday ? { border: '2px solid #ef4444', background: '#fee2e2' } : {}),
          opacity: isBusy ? 0.65 : 1,
          cursor: voidedToday || isBusy ? 'not-allowed' : 'grab',
          padding: variant === 'list' ? '10px 12px' : '10px 12px',
        }}
        title={noteTitle || undefined}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          {fracLabel ? (
            <span style={variant === 'list' ? st.fracTagList : st.fracTagFull}>{fracLabel}</span>
          ) : null}
          <div style={{ fontWeight: 1000, fontSize: 15, color: '#111', wordBreak: 'break-word', overflowWrap: 'anywhere', flex: '1 1 120px', minWidth: 0 }}>
            {name}
            {pairLabelSpan ? <span style={{ marginLeft: 6 }}>{pairLabelSpan}</span> : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: '0 1 auto' }}>
            {voidedToday ? (
              <span style={st.voidBadgeList}>
                {Number(b?.is_rejected_today) === 1 ? '今日已拒絕' : '今日臨時取消'}
              </span>
            ) : null}
            <div style={{ fontWeight: 1000, fontSize: 14, color: '#111', flex: '0 0 auto' }}>
              {t}
            </div>
          </div>
        </div>
        {metaRow}
        {noteText ? (
          <div style={st.cardNote}>
            <span style={st.cardNoteLabel}>備註</span>
            {noteText}
          </div>
        ) : null}
      </div>
    );
  };

  /** 1F 同一格：同一筆預約多張合併為一列，以 ×N 表示疊牌；拖曳／移除以「最上層」person_seq 為主 */
  const group1fSlotDisplayEntries = useCallback((rawList) => {
    const byBid = new Map();
    for (const { bid, ps } of rawList || []) {
      const b = bookingById.get(Number(bid));
      if (!b) continue;
      const pseq = normPlacementPersonSeq({ PERSON_SEQ: ps });
      if (!byBid.has(bid)) byBid.set(bid, { bid, b, seqs: [] });
      byBid.get(bid).seqs.push(pseq);
    }
    const out = [];
    for (const v of byBid.values()) {
      const uniq = [...new Set(v.seqs)].sort((a, z) => a - z);
      const stackCount = uniq.length;
      const topPs = uniq[uniq.length - 1];
      out.push({
        bid: v.bid,
        b: v.b,
        ps: topPs,
        stackCount,
        allPss: uniq,
      });
    }
    out.sort((a, z) => {
      const ta = bookingStartSortKey(a.b);
      const tb = bookingStartSortKey(z.b);
      if (ta !== tb) return ta - tb;
      return Number(a.bid) - Number(z.bid);
    });
    return out;
  }, [bookingById]);

  /** cellStyle: default | mergeCell（置於長方合併區內，邊框較輕） */
  const Slot = ({ slotId, label, cellStyle = 'default' }) => {
    const is1fSlot = /^1F-/i.test(String(slotId || ''));
    const raw = slotToEntries.get(slotId) || [];
    const entries = (() => {
      const arr = raw.map(({ bid, ps }) => ({ bid, ps, b: bookingById.get(Number(bid)) })).filter((x) => x.b);
      if (!is1fSlot) return arr.map((e) => ({ ...e, stackCount: 1, allPss: [e.ps] }));
      return group1fSlotDisplayEntries(arr.map(({ bid, ps }) => ({ bid, ps })));
    })();
    const hasAny = entries.length > 0;
    const physicalCount = raw.length;
    const bookingCount = new Set(raw.map((e) => e.bid)).size;
    const anyBusy = entries.some((x) => saving.has(saveKey(x.bid, x.ps)));
    const slotBase = cellStyle === 'mergeCell' ? st.slotMergeCell : st.slot;
    return (
      <div
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
        onDrop={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const payload = readDragPayload(e);
          if (!payload) return;
          markDropSucceeded();
          try {
            await postPlacement({ bookingId: payload.booking_id, slotId, personSeq: payload.person_seq });
          } catch {
            alert('保存失敗，請重試');
          }
        }}
        style={{
          ...slotBase,
          background: hasAny ? '#eef2ff' : '#fff',
          borderColor: hasAny ? '#6366f1' : cellStyle === 'mergeCell' ? 'rgba(99, 102, 241, 0.35)' : 'rgba(0,0,0,.16)',
          opacity: anyBusy ? 0.85 : 1,
        }}
      >
        {label ? (
          <div style={st.slotDeskLabel}>{label}</div>
        ) : null}
        {hasAny ? (
          <div style={st.slotMetaRow}>
            <span style={{ fontWeight: 900, color: '#6b7280', fontSize: 12 }}>
              {is1fSlot
                ? `預約 ${entries.reduce((sum, e) => sum + bookingHeadcount(e.b), 0)} 人 · ${bookingCount} 筆`
                : `${physicalCount} 人 · ${bookingCount} 筆`}
            </span>
          </div>
        ) : (
          <div style={st.slotEmptyHintOnly}>拖到這裡</div>
        )}
        <div style={st.slotCardsStack}>
          {entries.map(({ bid, ps, b, stackCount, allPss }) => (
            <div key={`${bid}-${(allPss || [ps]).join('-')}`} style={st.slotCardRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <BookingCard
                  b={b}
                  variant="slot"
                  personSeq={ps}
                  slotStackMult={is1fSlot ? stackCount : 1}
                />
              </div>
              <button
                type="button"
                disabled={saving.has(saveKey(bid, ps))}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const removePs = is1fSlot && Array.isArray(allPss) && allPss.length
                      ? allPss[allPss.length - 1]
                      : ps;
                    await postPlacement({ bookingId: Number(bid), slotId: null, personSeq: removePs });
                  } catch {
                    alert('移除失敗，請重試');
                  }
                }}
                style={{
                  ...st.slotPerRemoveBtn,
                  opacity: saving.has(saveKey(bid, ps)) ? 0.5 : 1,
                  cursor: saving.has(saveKey(bid, ps)) ? 'not-allowed' : 'pointer',
                }}
                title={is1fSlot ? '移回一張（由最上層開始）' : '此 1 人移回未放置'}
              >
                移除
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const tabBtn = (active) => ({
    padding: '8px 16px',
    borderRadius: 10,
    border: active ? '2px solid #111' : '1px solid rgba(0,0,0,.2)',
    background: active ? '#111' : '#fff',
    color: active ? '#fff' : '#111',
    fontWeight: 1000,
    fontSize: 13,
    cursor: 'pointer',
  });

  return (
    <div style={st.page}>
      <div style={st.topBar}>
        <div style={st.toolBar}>
        <div style={st.toolRow}>
          <div style={st.floorTabRow} role="tablist" aria-label="樓層">
            <button
              type="button"
              role="tab"
              aria-selected={floorTab === '1f'}
              style={tabBtn(floorTab === '1f')}
              onClick={() => setFloorTab('1f')}
            >
              1F
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={floorTab === '2f'}
              style={tabBtn(floorTab === '2f')}
              onClick={() => setFloorTab('2f')}
            >
              2F
            </button>
            {floorTab === '2f' ? (
              <>
                <button type="button" style={tabBtn(boardTab === 'seat')} onClick={() => setBoardTab('seat')}>
                  座位
                </button>
                <button type="button" style={tabBtn(boardTab === 'focus')} onClick={() => setBoardTab('focus')}>
                  專注
                </button>
              </>
            ) : null}
          </div>
          <div style={st.dateCtrl}>
            <button
              type="button"
              style={st.dateBtn}
              onClick={() => setDay(yyyyMMdd(new Date(parseYmd(day).getTime() - 86400000)))}
            >
              ←
            </button>
            <div style={{ fontWeight: 1000, fontSize: 13, minWidth: 92, textAlign: 'center', flex: '0 0 auto' }}>{dayLabel}</div>
            <button type="button" style={st.dateBtn} onClick={() => setDay(yyyyMMdd(new Date()))}>今日</button>
            <button
              type="button"
              style={st.dateBtn}
              onClick={() => setDay(yyyyMMdd(new Date(parseYmd(day).getTime() + 86400000)))}
            >
              →
            </button>
            <button type="button" style={st.refreshBtn} onClick={() => refresh(day)} disabled={loading}>重整</button>
            {floorTab === '2f' ? (
              <button
                type="button"
                style={st.autoBtn}
                onClick={runAutoLayout}
                disabled={loading || autoBusy}
                title="中間區：人數大先排；3～4 人同一 4 人組；1～2 人先上排 2 人組"
              >
                {autoBusy ? '試排中…' : '試排'}
              </button>
            ) : null}
          </div>
          {floorTab === '2f' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
              <label style={st.chkLabel}>
                <input
                  type="checkbox"
                  checked={zoneConfirm.seat}
                  disabled={zoneSaving}
                  onChange={(e) => postZoneConfirm(e.target.checked, zoneConfirm.focus)}
                />
                座位已確認
              </label>
              <label style={st.chkLabel}>
                <input
                  type="checkbox"
                  checked={zoneConfirm.focus}
                  disabled={zoneSaving}
                  onChange={(e) => postZoneConfirm(zoneConfirm.seat, e.target.checked)}
                />
                專注已確認
              </label>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          style={st.printBtn}
          onClick={printLayout3Pages}
          disabled={loading || printBusy}
          title="伺服器本機直接列印（3頁：1F / 2F座位 / 2F專注）。使用 SumatraPDF 時會請求長邊雙面；其他送印方式請手動勾選雙面。"
        >
          {printBusy ? '送印中…' : '列印擺放表(3頁)'}
        </button>
        </div>
        {err ? <div style={{ marginTop: 6, color: '#b91c1c', fontWeight: 900 }}>{err}</div> : null}
      </div>

      <div style={st.mainSplit}>
        <div style={st.boardColumn}>
          <div style={floorTab === '1f' ? st.boardWrapFloor1f : st.boardWrap}>
            {floorTab === '1f' ? (
              <section style={st.floorSection1f}>
                <div style={st.boardScrollY}>
                    <div style={st.floor1fGrid}>
                      <div style={st.floor1fLmGrid}>
                        <div style={{ ...st.colTitleBar, ...st.colTitleBar1f, ...st.floor1fLmCell }}>左（O1 · A1B1～A3B3 · A4）</div>
                        <div style={{ ...st.colTitleBar, ...st.colTitleBar1f, ...st.floor1fLmCell }}>中（A5B5 · A6B6）</div>
                        <div style={st.floor1fLmCell}>
                          <Slot slotId="1F-O1" label="O1" />
                        </div>
                        <div style={st.floor1fLmGhost} aria-hidden />
                        <div style={st.floor1fLmCell}>
                          <div style={st.floor1fLeftDivider}>
                            <Slot slotId="1F-A1B1" label="A1B1" />
                          </div>
                        </div>
                        <div style={st.floor1fLmGhost} aria-hidden />
                        <div style={st.floor1fLmCell}>
                          <Slot slotId="1F-A2B2" label="A2B2" />
                        </div>
                        <div style={st.floor1fLmCell}>
                          <div style={st.floor1fMergedLongRow}>
                            <Slot slotId="1F-A5" label="A5" cellStyle="mergeCell" />
                            <Slot slotId="1F-B5" label="B5" cellStyle="mergeCell" />
                          </div>
                        </div>
                        <div style={st.floor1fLmCell}>
                          <Slot slotId="1F-A3B3" label="A3B3" />
                        </div>
                        <div style={st.floor1fLmCell}>
                          <div style={st.floor1fMergedLongRow}>
                            <Slot slotId="1F-A6" label="A6" cellStyle="mergeCell" />
                            <Slot slotId="1F-B6" label="B6" cellStyle="mergeCell" />
                          </div>
                        </div>
                        <div style={st.floor1fLmRowSpacer} aria-hidden />
                        <div style={st.floor1fLmRowSpacer} aria-hidden />
                        <div style={st.floor1fLmCell}>
                          <div style={st.floor1fLeftA4Block}>
                            <Slot slotId="1F-A4" label="A4" />
                          </div>
                        </div>
                        <div style={st.floor1fLmGhost} aria-hidden />
                      </div>
                      <div style={st.floor1fColRight}>
                        <div style={{ ...st.colTitleBar, ...st.colTitleBar1f }}>右（A7 · A8B8 · A9～A13）</div>
                        <div style={st.floor1fRightAlignSpacer} aria-hidden />
                        <div style={st.floor1fRightTopGrid}>
                          <div style={st.floor1fRightA7Wrap}>
                            <Slot slotId="1F-A7" label="A7" />
                          </div>
                          <div style={st.floor1fRightA8b8Wrap}>
                            <Slot slotId="1F-A8B8" label="A8B8" />
                          </div>
                        </div>
                        <div style={{ ...st.floor1fRightDivider, ...st.floor1fStackColNatural }}>
                          <Slot slotId="1F-A9" label="A9" />
                          <Slot slotId="1F-A10" label="A10" />
                          <Slot slotId="1F-A11" label="A11" />
                          <Slot slotId="1F-A12" label="A12" />
                          <Slot slotId="1F-A13" label="A13" />
                        </div>
                      </div>
                    </div>
                </div>
              </section>
            ) : (
              <section style={st.floorSection}>
                {boardTab === 'seat' ? (
                  <div style={st.boardScrollY}>
                    <div style={st.seatsArea}>
                      <div style={st.leftCol}>
                        <div style={st.colTitleBar}>
                          高腳（3 格）
                        </div>
                        <div style={st.colSlotsNatural}>
                          {SLOT_GROUPS.left.map((s) => (
                            <Slot key={s.id} slotId={s.id} />
                          ))}
                        </div>
                      </div>

                      <div style={st.middleCol}>
                        <div style={st.colTitleBar}>
                          升降桌（5 組對座）
                        </div>
                        <div style={st.middlePairsStack}>
                          {MIDDLE_FACE_GROUPS.map((g) => (
                            <div key={g.row} style={st.middlePairBlock}>
                              <div style={st.middlePairGrid}>
                                {g.slots.map((s) => (
                                  <Slot key={s.id} slotId={s.id} label={s.label} />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={st.rightCol}>
                        <div style={st.colTitleBar}>
                          面牆位（5 格）
                        </div>
                        <div style={st.colSlotsNatural}>
                          {SLOT_GROUPS.right.map((s) => (
                            <Slot key={s.id} slotId={s.id} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={st.boardScrollY}>
                  <div style={st.focusBoard}>
                    <div style={st.focusZoneLabel}>2F-W 專注區面窗座位</div>
                    <div style={st.focusGrid6Wrap}>
                      <div style={st.focusGrid6}>
                        {FOCUS_WINDOW.map((s) => (
                          <Slot key={s.id} slotId={s.id} />
                        ))}
                      </div>
                    </div>
                    <div style={st.focusZoneLabel}>2F-Q 專注區大桌座位</div>
                    <div style={st.focusGrid10Wrap}>
                      <div style={st.focusGrid10}>
                        {FOCUS_TABLE.map((s) => (
                          <Slot key={s.id} slotId={s.id} />
                        ))}
                      </div>
                    </div>
                  </div>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </div>

      <div style={st.unplacedDock}>
        <DropZone
          title={`未放置（${unplacedPersons.length}）`}
          hint="左右滑動 · 拖到上方座位格"
          onDropBooking={async (bookingId, personSeq) => {
            try {
              await postPlacement({ bookingId, slotId: null, personSeq });
            } catch {
              alert('移除失敗，請重試');
            }
          }}
          dock
        >
          {loading ? (
            <div style={st.unplacedDockEmpty}>讀取中…</div>
          ) : unplacedPersons.length === 0 ? (
            <div style={st.unplacedDockEmpty}>（都已放置或今日無須排之預約）</div>
          ) : (
            <div style={st.unplacedStripScroll}>
              {unplacedPersons.map(({ b, personSeq }) => (
                <div key={`${b.ID}-${personSeq}`} style={st.unplacedStripCell}>
                  <BookingCard b={b} variant="dock" personSeq={personSeq} />
                </div>
              ))}
            </div>
          )}
        </DropZone>
      </div>
    </div>
  );
}

const st = {
  page: {
    height: '100%',
    minHeight: 0,
    padding: 8,
    boxSizing: 'border-box',
    fontFamily: 'system-ui, "Microsoft JhengHei", sans-serif',
    background: '#f6f5f4',
    color: '#111',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  topBar: {
    flexShrink: 0,
    background: '#fff',
    border: '1px solid rgba(0,0,0,.08)',
    borderRadius: 10,
    padding: '6px 8px',
  },
  toolBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  toolRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'nowrap',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    flex: '1 1 auto',
    minWidth: 0,
  },
  dateCtrl: { display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', flex: '0 0 auto' },
  dateBtn: {
    padding: '6px 10px',
    border: '1px solid rgba(0,0,0,.22)',
    borderRadius: 10,
    background: '#fff',
    fontWeight: 1000,
    fontSize: 11,
    cursor: 'pointer',
  },
  refreshBtn: {
    padding: '6px 10px',
    border: '1px solid rgba(0,0,0,.22)',
    borderRadius: 10,
    background: '#111',
    fontWeight: 1000,
    fontSize: 11,
    cursor: 'pointer',
    color: '#fff',
  },
  printBtn: {
    padding: '6px 10px',
    border: '1px solid rgba(0,0,0,.22)',
    borderRadius: 10,
    background: '#111',
    fontWeight: 1000,
    fontSize: 11,
    cursor: 'pointer',
    color: '#fff',
    flex: '0 0 auto',
    whiteSpace: 'nowrap',
  },
  autoBtn: {
    padding: '6px 10px',
    border: '1px solid #4f46e5',
    borderRadius: 10,
    background: '#4f46e5',
    fontWeight: 1000,
    fontSize: 11,
    cursor: 'pointer',
    color: '#fff',
  },
  chkLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
    userSelect: 'none',
  },

  /** 上方：座位／專注格（佔滿剩餘高度以便 1F 垂直置中） */
  mainSplit: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    width: '100%',
  },
  boardColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    width: '100%',
  },
  floorTabRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  colTitleBar: {
    fontWeight: 1000,
    fontSize: 12,
    marginBottom: 10,
    color: '#111',
    flexShrink: 0,
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
  },

  boardWrap: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,.08)',
    borderRadius: 12,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
    maxWidth: '100%',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  /** 1F：白板可捲動，避免垂直置中壓縮裁切 */
  boardWrapFloor1f: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,.08)',
    borderRadius: 12,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
    maxWidth: '100%',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  floorSection1f: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
  /** 座位白板：左右貼合畫面，僅上下捲動 */
  boardScrollY: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: 4,
  },
  floorSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  floorSectionTitle: {
    fontWeight: 1000,
    fontSize: 14,
    color: '#111',
    paddingBottom: 4,
    borderBottom: '1px solid rgba(0,0,0,.08)',
  },
  tabRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  seatHintRow: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },

  floor1fGrid: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 12,
    alignItems: 'flex-start',
    justifyContent: 'stretch',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: '4px 2px 10px',
    boxSizing: 'border-box',
  },
  /** 左＋中：同一 grid 對齊列（A2B2 與 A5B5 同列、A3B3 與 A6B6 同列） */
  floor1fLmGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gridAutoRows: 'auto',
    columnGap: 12,
    rowGap: 12,
    alignItems: 'stretch',
    minWidth: 0,
    flex: '1 1 58%',
    maxWidth: '100%',
  },
  floor1fLmCell: {
    minWidth: 0,
  },
  floor1fLmGhost: {
    minWidth: 0,
    minHeight: 0,
  },
  floor1fLmRowSpacer: {
    minWidth: 0,
    minHeight: 0,
  },
  floor1fLeftDivider: {
    marginTop: 6,
    paddingTop: 10,
    borderTop: '1px dashed rgba(99, 102, 241, 0.35)',
  },
  /** 僅 1F：欄標題水平置中 */
  colTitleBar1f: {
    justifyContent: 'center',
    textAlign: 'center',
  },
  floor1fColRight: {
    flex: '1 1 42%',
    minWidth: 0,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'stretch',
  },
  /** 讓 A7 對齊左側 A1B1（上方預留 O1 列高度） */
  floor1fRightAlignSpacer: {
    flexShrink: 0,
    minHeight: 88,
    height: 88,
    border: '1px dashed rgba(0,0,0,.0)',
    borderRadius: 14,
  },
  /** 右欄上半部：A7 半格（左）＋ A8B8 長條（右，下偏一點） */
  floor1fRightTopGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 12,
    alignItems: 'start',
    width: '100%',
  },
  floor1fRightA7Wrap: {
    minWidth: 0,
    alignSelf: 'start',
  },
  floor1fRightA8b8Wrap: {
    minWidth: 0,
    alignSelf: 'start',
    marginTop: 10,
  },
  floor1fRightDivider: {
    marginTop: 6,
    paddingTop: 10,
    borderTop: '1px dashed rgba(99, 102, 241, 0.35)',
    width: '72%',
    alignSelf: 'center',
  },
  /** A1B1／A2B2／A3B3：外框一個長方區，內為 A／B 兩格 */
  floor1fMergedLongRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 12,
    padding: 10,
    border: '2px solid rgba(99, 102, 241, 0.38)',
    borderRadius: 16,
    background: 'linear-gradient(180deg, #fafbff 0%, #fff 100%)',
    boxSizing: 'border-box',
    alignItems: 'stretch',
  },
  floor1fLeftA4Block: {
    flexShrink: 0,
    width: '100%',
    marginTop: 4,
    paddingTop: 10,
    borderTop: '1px dashed rgba(99, 102, 241, 0.35)',
  },
  /** 右欄 A8／B8：併排、間距略縮以利辨識 */
  floor1fPairRowTight: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
    alignItems: 'stretch',
    width: '100%',
  },
  /** 右欄 A9～A13：自然高度，不壓縮 */
  floor1fStackColNatural: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'stretch',
    width: '100%',
    flexShrink: 0,
  },
  slotDeskLabel: {
    fontWeight: 1000,
    fontSize: 11,
    color: '#111',
    marginBottom: 6,
    letterSpacing: 0.02,
    textAlign: 'center',
  },

  seatsArea: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.45fr) minmax(0, 1fr)',
    columnGap: 12,
    rowGap: 12,
    alignItems: 'start',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '2px 2px 8px',
  },
  leftCol: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    minHeight: 0,
  },
  middleCol: {
    minWidth: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'start',
    minHeight: 0,
  },
  rightCol: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    minHeight: 0,
  },
  /** 左右欄：自然高度，不壓縮中間區 */
  colSlotsNatural: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flexShrink: 0,
  },
  middlePairsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flexShrink: 0,
    width: '100%',
  },
  middlePairBlock: {
    border: '2px solid rgba(99, 102, 241, 0.38)',
    borderRadius: 14,
    padding: '12px 12px 14px',
    background: 'linear-gradient(180deg, #fafbff 0%, #fff 100%)',
    boxSizing: 'border-box',
    flexShrink: 0,
    width: '100%',
  },
  middlePairGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    alignItems: 'stretch',
  },

  focusBoard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minHeight: 0,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  focusZoneLabel: {
    fontWeight: 1000,
    fontSize: 11,
    color: '#374151',
    marginBottom: 0,
    flexShrink: 0,
  },
  focusGrid6Wrap: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
    minWidth: 0,
  },
  focusGrid6: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gap: 10,
    width: '100%',
    minWidth: 0,
  },
  focusGrid10Wrap: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
    minWidth: 0,
  },
  focusGrid10: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 10,
    width: '100%',
    minWidth: 0,
  },

  /** 未放置：固定於畫面下方，橫向捲動；備註可完整換行顯示 */
  unplacedDock: {
    flex: '0 0 auto',
    width: '100%',
    flexShrink: 0,
    borderTop: '1px solid rgba(0,0,0,.1)',
    paddingTop: 8,
    background: '#f6f5f4',
    boxShadow: '0 -6px 16px rgba(0,0,0,.06)',
  },
  unplacedDockEmpty: {
    padding: '8px 4px',
    color: '#374151',
    fontWeight: 900,
    fontSize: 12,
  },
  unplacedStripScroll: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 10,
    alignItems: 'stretch',
    overflowX: 'auto',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    maxHeight: 260,
    padding: '2px 4px 6px',
    width: '100%',
    boxSizing: 'border-box',
  },
  unplacedStripCell: {
    flex: '0 0 auto',
    width: 'clamp(200px, 32vw, 280px)',
    minWidth: 0,
  },
  zoneDock: {
    border: '1px solid rgba(0,0,0,.10)',
    borderRadius: 12,
    background: '#fff',
    overflow: 'hidden',
  },
  zoneTitleRowDock: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    padding: '6px 10px',
    background: '#fff',
    borderBottom: '1px solid rgba(0,0,0,.08)',
  },
  zoneBodyDock: {
    padding: '6px 8px 8px',
  },
  cardDock: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,.10)',
    borderRadius: 10,
    boxShadow: '0 2px 6px rgba(0,0,0,.06)',
    userSelect: 'none',
    padding: '8px 10px',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  cardDockLine1: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 6,
    minWidth: 0,
  },
  cardDockLine2: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
    minWidth: 0,
    fontSize: 11,
    fontWeight: 900,
    color: '#374151',
  },
  cardDockTime: {
    fontWeight: 1000,
    fontSize: 13,
    color: '#111',
    flex: '0 0 auto',
  },
  cardDockHeadcount: {
    flex: '0 0 auto',
    fontWeight: 900,
    fontSize: 12,
    color: '#b45309',
    background: '#fff7ed',
    border: '1px solid #fde68a',
    borderRadius: 6,
    padding: '1px 6px',
    whiteSpace: 'nowrap',
  },
  cardDockName: {
    fontWeight: 1000,
    fontSize: 13,
    color: '#111',
    minWidth: 0,
    flex: '1 1 auto',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  cardDockSeat: {
    minWidth: 0,
    flex: '1 1 auto',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  cardDockPhone: {
    flex: '0 0 auto',
    color: '#6b7280',
    whiteSpace: 'nowrap',
  },
  cardDockNote: {
    fontSize: 11,
    fontWeight: 800,
    color: '#4b5563',
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    marginTop: 2,
    paddingTop: 4,
    borderTop: '1px solid rgba(0,0,0,.06)',
  },
  fracTagDock: {
    flex: '0 0 auto',
    fontWeight: 1000,
    fontSize: 11,
    color: '#4f46e5',
  },
  voidBadgeDock: {
    flex: '0 0 auto',
    fontSize: 9,
    fontWeight: 900,
    color: '#b91c1c',
    border: '1px solid #ef4444',
    padding: '1px 5px',
    borderRadius: 999,
    background: '#fee2e2',
  },

  slot: {
    border: '3px dashed rgba(0,0,0,.24)',
    borderRadius: 14,
    padding: 12,
    minHeight: 72,
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  /** 置於 floor1fMergedLongRow 內的半格 */
  slotMergeCell: {
    border: '2px dashed rgba(99, 102, 241, 0.42)',
    borderRadius: 12,
    padding: 10,
    minHeight: 68,
    boxSizing: 'border-box',
    flexShrink: 0,
    minWidth: 0,
  },
  slotCardsStack: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  slotCardRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  slotPerRemoveBtn: {
    flex: '0 0 auto',
    padding: '4px 8px',
    borderRadius: 8,
    border: '1px solid rgba(0,0,0,.2)',
    background: '#fff',
    fontWeight: 1000,
    fontSize: 10,
    marginTop: 4,
  },
  slotMetaRow: { marginBottom: 6 },
  slotEmptyHintOnly: {
    fontSize: 11,
    fontWeight: 900,
    color: '#9ca3af',
    marginBottom: 6,
    textAlign: 'center',
  },
  slotClearBtn: {
    padding: '4px 8px',
    borderRadius: 999,
    border: '1px solid rgba(0,0,0,.2)',
    background: '#fff',
    fontWeight: 1000,
    fontSize: 11,
  },

  card: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,.10)',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,.06)',
    userSelect: 'none',
  },
  cardSlot: {
    background: '#fff',
    border: '1px solid rgba(99,102,241,.35)',
    borderRadius: 10,
    padding: '8px 10px',
    userSelect: 'none',
    textAlign: 'left',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  cardSlotMulti: {
    padding: '8px 8px',
  },
  cardSlotFracStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  cardSlotFracRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  /** 座位格：第 0 行僅「幾分之幾」 */
  cardSlotRow0Frac: {
    textAlign: 'center',
    paddingBottom: 0,
  },
  fracTagRow0: {
    fontWeight: 1000,
    fontSize: 13,
    color: '#4f46e5',
    lineHeight: 1.35,
    letterSpacing: 0.02,
  },
  fracTag: {
    flex: '0 0 auto',
    fontWeight: 1000,
    fontSize: 12,
    color: '#4f46e5',
    lineHeight: 1.35,
    paddingTop: 2,
  },
  fracTagList: {
    flex: '0 0 auto',
    fontWeight: 1000,
    fontSize: 12,
    color: '#4f46e5',
    lineHeight: 1.35,
    marginTop: 2,
  },
  fracTagFull: {
    flex: '0 0 auto',
    fontWeight: 1000,
    fontSize: 13,
    color: '#4f46e5',
    lineHeight: 1.35,
    marginTop: 2,
  },
  multiFracBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  multiFracLine: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  multiFracLineDivider: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid rgba(0,0,0,.08)',
  },
  cardSlotTopLine: {
    display: 'flex',
    gap: 8,
    alignItems: 'baseline',
    justifyContent: 'space-between',
    minWidth: 0,
  },
  cardSlotBottomLine: {
    display: 'flex',
    gap: 10,
    alignItems: 'baseline',
    justifyContent: 'space-between',
    minWidth: 0,
  },
  cardSlotTimeInline: { fontWeight: 1000, fontSize: 14, color: '#111', lineHeight: 1.35, flex: '0 0 auto' },
  cardSlotNameInline: { fontWeight: 900, fontSize: 12, color: '#374151', lineHeight: 1.4, minWidth: 0, flex: '1 1 auto', textAlign: 'right', wordBreak: 'break-word', overflowWrap: 'anywhere' },
  cardSlotSeatInline: { fontWeight: 800, fontSize: 11, color: '#6b7280', lineHeight: 1.35, minWidth: 0, flex: '1 1 auto', wordBreak: 'break-word', overflowWrap: 'anywhere' },
  cardSlotHeadcountInline: { fontWeight: 900, fontSize: 11, color: '#111', lineHeight: 1.35, flex: '0 0 auto', whiteSpace: 'nowrap' },
  cardSlotHeadcountBadge: {
    flex: '0 0 auto',
    fontWeight: 900,
    fontSize: 11,
    color: '#b45309',
    background: '#fff7ed',
    border: '1px solid #fde68a',
    borderRadius: 6,
    padding: '1px 6px',
    whiteSpace: 'nowrap',
    lineHeight: 1.35,
  },
  cardSlotNoteInline: {
    marginTop: 6,
    paddingTop: 6,
    borderTop: '1px solid rgba(99,102,241,.22)',
    fontSize: 10,
    fontWeight: 800,
    color: '#4b5563',
    lineHeight: 1.45,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  cardNoteLabel: {
    display: 'inline-block',
    marginRight: 6,
    marginBottom: 2,
    fontWeight: 1000,
    fontSize: 10,
    color: '#92400e',
    background: '#fef3c7',
    padding: '1px 6px',
    borderRadius: 4,
    verticalAlign: 'middle',
  },
  voidBadgeSlot: {
    fontSize: 10,
    fontWeight: 900,
    color: '#b91c1c',
    border: '1px solid #ef4444',
    padding: '2px 6px',
    borderRadius: 999,
    background: '#fee2e2',
    whiteSpace: 'nowrap',
    flex: '0 0 auto',
  },
  voidBadgeList: {
    fontSize: 11,
    fontWeight: 900,
    color: '#b91c1c',
    border: '1.5px solid #ef4444',
    padding: '2px 8px',
    borderRadius: 999,
    background: '#fee2e2',
    whiteSpace: 'nowrap',
  },
  cardSlotNote: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid rgba(99,102,241,.22)',
    fontSize: 10,
    fontWeight: 800,
    color: '#4b5563',
    lineHeight: 1.45,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  cardNote: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid rgba(0,0,0,.08)',
    fontSize: 11,
    fontWeight: 800,
    color: '#4b5563',
    lineHeight: 1.45,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  zone: {
    border: '1px solid rgba(0,0,0,.10)',
    borderRadius: 12,
    background: '#f9fafb',
    overflow: 'hidden',
  },
  zoneTitleRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    padding: '10px 12px',
    background: '#fff',
    borderBottom: '1px solid rgba(0,0,0,.08)',
  },
  zoneTitle: { fontWeight: 1000, fontSize: 12 },
  zoneHint: { fontSize: 11, fontWeight: 900, color: '#6b7280' },
  zoneBody: { padding: 12 },
};
